import React from 'react'
import truncate from 'lodash.truncate'
import styled from 'styled-components'
import { Link as RouterLink } from 'react-router-dom'
import { Text } from 'components/Box'
import BasePanel from 'components/Panel'
import Button, { FlatButton } from 'components/Button'
import { truncateLines } from 'components/Truncate'
import { Inviter } from './types'
import Avatar from 'components/Avatar'
import { breakpoint } from 'utils'
import { transparentize } from 'polished'
import IndexLayout from 'components/IndexLayout'

export const headingTextForInvite = (
  inviterName?: string,
  documentName?: string
) => {
  const formattedDocumentName = `“${documentName}”`

  if (inviterName) {
    return (
      <>
        <strong>{inviterName}</strong> wants to share a document with you
      </>
    )
  }

  return `You have been invited to join ${
    documentName ? formattedDocumentName : 'this document'
  }`
}

export const Panel = styled(BasePanel)`
  max-width: 100%;
`

export const BodyPanel = styled(BasePanel.Body)`
  ${breakpoint('xs')`
    padding: 16px;
  `}
`

export const MainAction = styled(Button)`
  && {
    margin-bottom: 24px;
  }
`

export const SecondaryAction = styled(Button)`
  && {
    margin-left: 0;
  }
`

export const SecondaryActionLink = styled(FlatButton).attrs({
  variant: 'secondary-untinted',
})`
  && {
    margin-left: 0;
  }

  text-decoration: underline;
  font-size: ${({ theme }) => theme.fontSizes.small};
`

export const WordBreak = styled.div`
  word-wrap: break-word;
`

export const Line = styled.div`
  width: 100%;
  margin-top: 32px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.dark.base};
`

export const SSOLink = styled(RouterLink)`
  margin-top: 24px;
  font-size: 0.875rem;
  text-align: center;
  display: block;
`

interface HeadingProps {
  inviter?: Inviter
  documentName?: string
}

export const Content = styled(Text).attrs({
  'aria-label': 'content-explanation',
})`
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.foreground.secondary[3]};
  font-size: ${({ theme }) => theme.fontSizes.small};

  strong {
    color: ${({ theme }) => theme.colors.foreground.secondary[1]};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }
`

export const ContentTitle = styled(Content)`
  margin-top: ${({ theme }) => theme.space[4]}px;
  margin-bottom: ${({ theme }) => theme.space[8]}px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.body};
`

export const Heading = ({ inviter, documentName }: HeadingProps) => (
  <ContentTitle>
    <WordBreak>
      {headingTextForInvite(
        inviter?.name || undefined,
        truncate(documentName, { length: 80 })
      )}
    </WordBreak>
  </ContentTitle>
)

const WrapperName = styled.div`
  display: inline-flex;
  white-space: pre-wrap;
  align-items: baseline;
  margin-left: 4px;
`

const AvatarName = styled.span`
  word-break: break-word;
  ${truncateLines(3)};
  color: ${({ theme }) => theme.colors.foreground.secondary[1]};

  margin-left: 8px;
`

const StyledAvatar = styled(Avatar)`
  align-self: center;
`

export const NameWithAvatar = ({
  name,
  src,
}: {
  name: string
  src?: string | null
}) => (
  <WrapperName>
    <StyledAvatar size="16px" src={src || ''} name={name} />
    <AvatarName>
      <strong>{name}</strong>
    </AvatarName>
  </WrapperName>
)

export const PanelFooter = styled(BasePanel.Footer)``

export const InvitedEmail = styled.span`
  background-color: ${({ theme }) =>
    transparentize(1 - 0.2, theme.colors.tint[5])};
`

export const StyledIndexLayout = styled(IndexLayout)`
  main {
    max-width: 512px;
    margin-top: 0;
    flex: 1;
    align-items: center;
    display: flex;
  }
`
